import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

interface PdfExportOptions {
    filename?: string;
    format?: 'a4' | 'letter' | 'legal';
    orientation?: 'portrait' | 'landscape';
    margin?: number;
    scale?: number;
    quality?: number;
    backgroundColor?: string;
    onProgress?: (progress: number) => void;
    onError?: (error: Error) => void;
}

export class EnhancedPdfExporter {
    private static readonly DEFAULT_OPTIONS: Required<PdfExportOptions> = {
        filename: 'document.pdf',
        format: 'a4',
        orientation: 'portrait',
        margin: 10,
        scale: 2,
        quality: 1.0,
        backgroundColor: '#ffffff',
        onProgress: () => {},
        onError: (error) => console.error('PDF Export Error:', error)
    };

    /**
     * Enhanced PDF export with better quality, error handling, and multi-page support
     */
    static async exportElementToPdf(
        selector: string | HTMLElement,
        options: PdfExportOptions = {}
    ): Promise<void> {
        const config = { ...this.DEFAULT_OPTIONS, ...options };

        try {
            config.onProgress(10);

            // Get element
            const element = this.getElement(selector);
            if (!element) {
                throw new Error('Element not found for PDF export');
            }

            config.onProgress(20);

            // Prepare element for export
            await this.prepareElementForExport(element);

            config.onProgress(30);

            // Create high-quality canvas
            const canvas = await this.createHighQualityCanvas(element, config);

            config.onProgress(60);

            // Generate PDF with proper page handling
            const pdf = await this.generatePdfFromCanvas(canvas, config);

            config.onProgress(90);

            // Save PDF
            pdf.save(config.filename);

            config.onProgress(100);

        } catch (error) {
            config.onError(error as Error);
            throw error;
        }
    }

    /**
     * Export multiple elements to a single PDF
     */
    static async exportMultipleElementsToPdf(
        selectors: (string | HTMLElement)[],
        options: PdfExportOptions = {}
    ): Promise<void> {
        const config = { ...this.DEFAULT_OPTIONS, ...options };

        try {
            const pdf = new jsPDF({
                orientation: config.orientation === 'portrait' ? 'p' : 'l',
                unit: 'mm',
                format: config.format
            });

            let isFirstPage = true;

            for (let i = 0; i < selectors.length; i++) {
                const element = this.getElement(selectors[i]);
                if (!element) continue;

                config.onProgress((i / selectors.length) * 80);

                await this.prepareElementForExport(element);
                const canvas = await this.createHighQualityCanvas(element, config);

                if (!isFirstPage) {
                    pdf.addPage();
                }

                await this.addCanvasToPdf(pdf, canvas, config);
                isFirstPage = false;
            }

            config.onProgress(90);
            pdf.save(config.filename);
            config.onProgress(100);

        } catch (error) {
            config.onError(error as Error);
            throw error;
        }
    }

    private static getElement(selector: string | HTMLElement): HTMLElement | null {
        if (typeof selector === 'string') {
            return document.querySelector(selector) as HTMLElement;
        }
        return selector;
    }

    private static async prepareElementForExport(element: HTMLElement): Promise<void> {
        // Ensure all images are loaded
        const images = element.querySelectorAll('img');
        const imagePromises = Array.from(images).map(img => {
            return new Promise((resolve) => {
                if (img.complete) {
                    resolve(img);
                } else {
                    img.onload = () => resolve(img);
                    img.onerror = () => resolve(img);
                }
            });
        });

        await Promise.all(imagePromises);

        // Force layout recalculation
        element.style.visibility = 'visible';
        element.style.display = 'block';

        // Wait for any CSS transitions/animations to complete
        await new Promise(resolve => setTimeout(resolve, 100));
    }

    private static async createHighQualityCanvas(
        element: HTMLElement,
        config: Required<PdfExportOptions>
    ): Promise<HTMLCanvasElement> {
        const canvas = await html2canvas(element, {
            scale: config.scale,
            useCORS: true,
            allowTaint: false,
            logging: false,
            backgroundColor: config.backgroundColor,
            removeContainer: true,
            imageTimeout: 15000,
            onclone: (clonedDoc) => {
                // Ensure proper styling in cloned document
                const clonedElement = clonedDoc.querySelector(element.tagName) as HTMLElement;
                if (clonedElement) {
                    clonedElement.style.transform = 'none';
                    clonedElement.style.animation = 'none';
                }
            }
        });

        return canvas;
    }

    private static async generatePdfFromCanvas(
        canvas: HTMLCanvasElement,
        config: Required<PdfExportOptions>
    ): Promise<jsPDF> {
        const pdf = new jsPDF({
            orientation: config.orientation === 'portrait' ? 'p' : 'l',
            unit: 'mm',
            format: config.format
        });

        await this.addCanvasToPdf(pdf, canvas, config);
        return pdf;
    }

    private static async addCanvasToPdf(
        pdf: jsPDF,
        canvas: HTMLCanvasElement,
        config: Required<PdfExportOptions>
    ): Promise<void> {
        const imgData = canvas.toDataURL('image/jpeg', config.quality);

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const contentWidth = pageWidth - (config.margin * 2);
        const contentHeight = pageHeight - (config.margin * 2);

        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = config.margin;
        let pageNumber = 1;

        // Add first page
        pdf.addImage(imgData, 'JPEG', config.margin, position, imgWidth, imgHeight);
        heightLeft -= contentHeight;

        // Add additional pages if needed
        while (heightLeft > 0) {
            position = -(pageHeight * pageNumber) + config.margin;
            pdf.addPage();
            pdf.addImage(imgData, 'JPEG', config.margin, position, imgWidth, imgHeight);
            heightLeft -= contentHeight;
            pageNumber++;
        }
    }

    /**
     * Utility method to get optimal PDF dimensions for an element
     */
    static getOptimalPdfDimensions(element: HTMLElement): {
        format: 'a4' | 'letter';
        orientation: 'portrait' | 'landscape';
    } {
        const rect = element.getBoundingClientRect();
        const aspectRatio = rect.width / rect.height;

        if (aspectRatio > 1.2) {
            return { format: 'a4', orientation: 'landscape' };
        } else {
            return { format: 'a4', orientation: 'portrait' };
        }
    }
}
